import { useMemo } from "react";
import { useGetList, type Identifier } from "ra-core";
import { useFormContext } from "react-hook-form";
import { Button } from "@/components/ui/button";
import type { Company, Contact } from "@/components/atomic-crm/types";
import type { Ticket } from "@/modules/types";
import {
  getTicketSuggestionHints,
  websiteMatchesDomain,
} from "@/modules/tickets/ticketClientSuggestion";

type Suggestion = { companyId: Identifier; name: string; reason: string };

/** Suggested accounts for an unlinked ticket; linking stays a user action. */
export const TicketClientSuggestions = ({ ticket }: { ticket: Ticket }) => {
  const { setValue } = useFormContext();
  const { addressPattern, domain } = getTicketSuggestionHints(ticket);

  const { data: addressCompanies = [] } = useGetList<Company>(
    "companies",
    {
      filter: { "address@ilike": addressPattern },
      pagination: { page: 1, perPage: 3 },
      sort: { field: "name", order: "ASC" },
    },
    { enabled: Boolean(addressPattern), staleTime: 60_000 },
  );
  const { data: addressContacts = [] } = useGetList<Contact>(
    "contacts",
    {
      filter: { "address@ilike": addressPattern },
      pagination: { page: 1, perPage: 3 },
      sort: { field: "last_name", order: "ASC" },
    },
    { enabled: Boolean(addressPattern), staleTime: 60_000 },
  );
  const { data: domainCompanies = [] } = useGetList<Company>(
    "companies",
    {
      filter: { "website@ilike": `%${domain}%` },
      pagination: { page: 1, perPage: 5 },
      sort: { field: "name", order: "ASC" },
    },
    { enabled: Boolean(domain), staleTime: 60_000 },
  );

  const suggestions = useMemo(() => {
    const list: Suggestion[] = [];
    const add = (
      companyId: Identifier | null | undefined,
      name: string,
      reason: string,
    ) => {
      if (companyId == null) return;
      if (list.some((entry) => String(entry.companyId) === String(companyId)))
        return;
      list.push({ companyId, name, reason });
    };
    for (const company of addressCompanies)
      add(company.id, company.name, "matches the property address");
    for (const contact of addressContacts)
      add(
        contact.company_id,
        contact.company_name ?? "Contact's account",
        "a contact there has this property address",
      );
    for (const company of domainCompanies)
      if (domain && websiteMatchesDomain(company.website, domain))
        add(company.id, company.name, `website matches ${domain}`);
    return list.slice(0, 3);
  }, [addressCompanies, addressContacts, domainCompanies, domain]);

  if (!suggestions.length) return null;

  return (
    <div className="space-y-2 rounded-md border px-3 py-2.5">
      <p className="text-xs text-muted-foreground">
        Possible account — not linked automatically:
      </p>
      {suggestions.map((suggestion) => (
        <div
          key={String(suggestion.companyId)}
          className="flex items-center justify-between gap-3"
        >
          <div className="min-w-0 text-sm">
            <span className="block truncate font-medium">
              {suggestion.name}
            </span>
            <span className="block truncate text-xs text-muted-foreground">
              {suggestion.reason}
            </span>
          </div>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() =>
              setValue("company_id", suggestion.companyId, {
                shouldDirty: true,
              })
            }
          >
            Link
          </Button>
        </div>
      ))}
    </div>
  );
};
